/**
 * 마법사 템플릿 — 유형별로 자주 쓰는 옵션 조합을 미리 담아 둔 것.
 * 유형을 고르면 그 유형의 템플릿이 (최대 4개) 나열되고, 고르면 옵션·적재 방식이 한 번에 채워진다.
 * 템플릿 선택은 선택 사항이라 고르지 않아도 다음 단계로 갈 수 있다.
 *
 * 지금은 코드에 담긴 기본 제공 템플릿뿐이다. 사용자 정의 템플릿(헤더 '템플릿 정의')은
 * 같은 모양(WizardTemplate)을 저장소에서 읽어 이 목록 뒤에 붙이는 방향으로 확장한다.
 */
import { WizardSpec, FlowKind } from './buildWorkflow';
import i18n from '../../../i18n';

export interface WizardTemplate {
  id: string;
  kind: FlowKind;
  /** 사용자 정의 템플릿의 이름·설명 — 기본 제공은 번역 키(wizard:template.<id>.name/desc)에서 읽는다 */
  name?: string;
  /** 한 줄 설명 — 카드에 표시 */
  desc?: string;
  icon: string;
  /** 기본 제공(코드) / 사용자 정의(저장소) */
  builtin: boolean;
  /** 템플릿이 채우는 값 — 유형·이름은 건드리지 않는다 */
  apply: (s: WizardSpec) => WizardSpec;
}

const basic = (s: WizardSpec): WizardSpec => ({ ...s, option: 'basic' });

const TEMPLATES: WizardTemplate[] = [
  // ── DB → DB
  {
    id: 'db2db_standard', kind: 'db2db', icon: '🔄', builtin: true,
    apply: (s) => ({ ...basic(s), load: { ...s.load, mode: 'WRITE', useBulkLoader: false } }),
  },
  {
    id: 'db2db_verify', kind: 'db2db', icon: '🛡️', builtin: true,
    apply: (s) => ({ ...s, option: 'verify', load: { ...s.load, mode: 'WRITE' } }),
  },
  {
    id: 'db2db_partition', kind: 'db2db', icon: '🧩', builtin: true,
    apply: (s) => ({ ...s, option: 'partition', partition: { ...s.partition, mode: 'range', count: 8 } }),
  },
  {
    id: 'db2db_sql', kind: 'db2db', icon: '🧾', builtin: true,
    apply: (s) => ({ ...basic(s), source: { ...s.source, mode: 'sql' }, target: { ...s.target, mode: 'sql' } }),
  },

  // ── SAM파일 → DB
  {
    id: 'file2db_standard', kind: 'file2db', icon: '📄', builtin: true,
    apply: (s) => ({ ...basic(s), load: { ...s.load, mode: 'WRITE' } }),
  },
  {
    id: 'file2db_append', kind: 'file2db', icon: '➕', builtin: true,
    apply: (s) => ({ ...basic(s), load: { ...s.load, mode: 'APPEND' } }),
  },
  {
    id: 'file2db_upsert', kind: 'file2db', icon: '🔑', builtin: true,
    apply: (s) => ({ ...basic(s), load: { ...s.load, mode: 'UPSERT' } }),
  },
  {
    id: 'file2db_verify', kind: 'file2db', icon: '🛡️', builtin: true,
    apply: (s) => ({ ...s, option: 'verify', load: { ...s.load, mode: 'WRITE' } }),
  },

  // ── DB → SAM파일
  {
    id: 'db2file_extract', kind: 'db2file', icon: '🗂', builtin: true,
    apply: basic,
  },
  {
    id: 'db2file_verify', kind: 'db2file', icon: '🛡️', builtin: true,
    apply: (s) => ({ ...s, option: 'verify' }),
  },
  {
    id: 'db2file_sql', kind: 'db2file', icon: '🧾', builtin: true,
    apply: (s) => ({ ...basic(s), source: { ...s.source, mode: 'sql' } }),
  },

  // ── SAM파일 → SAM파일
  {
    id: 'file2file_copy', kind: 'file2file', icon: '📑', builtin: true,
    apply: basic,
  },
  {
    id: 'file2file_verify', kind: 'file2file', icon: '🛡️', builtin: true,
    apply: (s) => ({ ...s, option: 'verify' }),
  },
];

/** 유형별 템플릿 — 화면에는 최대 4개까지만 보여 준다 */
export function templatesFor(kind: FlowKind, limit = 4): WizardTemplate[] {
  return TEMPLATES.filter((t) => t.kind === kind).slice(0, limit);
}

export function allTemplates(): WizardTemplate[] {
  return TEMPLATES;
}

/** 템플릿 이름·설명 — 렌더할 때 부른다 */
export const templateName = (tp: WizardTemplate) => (tp.builtin ? i18n.t(`wizard:template.${tp.id}.name`) : tp.name || tp.id);
export const templateDesc = (tp: WizardTemplate) => (tp.builtin ? i18n.t(`wizard:template.${tp.id}.desc`) : tp.desc || '');
